import { useEffect, useRef, useState } from "react";
import { useAuthenticatedUser } from "@/auth/auth-context";
import type { User } from "@/lib/api/schemas";
import { getUserFacingError } from "@/lib/api/errors";
import { api } from "@/lib/api/services";
import { useT } from "@/lib/i18n";
import type { Notice } from "@/lib/notices";
import { notices } from "@/lib/notices";
import { describePopup, getPopupKind, getPopupCode, getRestrictionKind } from "@/lib/popup-notices";
import { NoticeModal } from "./notice-modal";
import { ErrorState } from "./ui/page-state";

type QueuedNotice = { key: string; notice: Notice; restriction: boolean };

export function PopupNotices() {
  const { user, refreshUser } = useAuthenticatedUser();
  return <AccountPopupNotices key={String(user.id)} user={user} refreshUser={refreshUser} />;
}

function AccountPopupNotices({ user, refreshUser }: { user: User; refreshUser: () => Promise<User | null> }) {
  const t = useT();
  const [queue, setQueue] = useState<QueuedNotice[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [retry, setRetry] = useState(0);
  const seen = useRef(new Set<string>());
  const unreadPopups = user.unread_popup_notifications_count;
  const unreadNotifications = user.unread_notifications_count ?? 0;
  const previousNotifications = useRef<number | null>(null);

  useEffect(() => {
    let stopped = false;
    let pending = false;
    const poll = async () => {
      if (stopped || document.hidden || pending) return;
      pending = true;
      try { await refreshUser(); }
      catch (failure) { if (!stopped) setError(failure); }
      finally { pending = false; }
    };
    const timer = window.setInterval(() => void poll(), 30_000);
    const onVisible = () => { if (!document.hidden) void poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [refreshUser]);

  useEffect(() => {
    if (unreadPopups !== undefined && unreadPopups <= 0) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      api.getPopupNotifications(controller.signal).then(
        (items) => {
          if (controller.signal.aborted) return;
          const next: QueuedNotice[] = [];
          for (const popup of items) {
            const kind = getPopupKind(popup);
            const notice = describePopup(popup);
            if (kind === "generic" && notice.paragraphs.length === 0 && "key" in notice.title) continue;
            const key = kind === "generic" ? `popup:${String(popup.id ?? JSON.stringify([popup.title, popup.text, popup.description]))}` : `restriction:${kind}`;
            if (seen.current.has(key)) continue;
            seen.current.add(key);
            next.push({ key, notice, restriction: kind !== "generic" });
          }
          setQueue((current) => [...current, ...next]);
          setError(null);
        },
        (failure: unknown) => { if (!controller.signal.aborted) setError(failure); }
      );
    }, 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [unreadPopups, retry]);

  useEffect(() => {
    const previous = previousNotifications.current;
    if (unreadNotifications <= 0) { previousNotifications.current = null; return; }
    if (retry === 0 && (previous === unreadNotifications || (previous !== null && unreadNotifications < previous))) return;
    const controller = new AbortController();
    api.getNotifications(false, controller.signal).then(
      (rows) => {
        if (controller.signal.aborted) return;
        previousNotifications.current = unreadNotifications;
        const next: QueuedNotice[] = [];
        for (const row of rows.slice(0, unreadNotifications)) {
          const kind = getRestrictionKind(getPopupCode(row));
          if (!kind || seen.current.has(`restriction:${kind}`)) continue;
          seen.current.add(`restriction:${kind}`);
          next.push({ key: `restriction:${kind}`, notice: notices[kind], restriction: true });
        }
        setQueue((current) => [...current, ...next]);
      },
      (failure: unknown) => { if (!controller.signal.aborted) setError(failure); }
    );
    return () => controller.abort();
  }, [unreadNotifications, retry]);

  const current = queue[0];
  const remaining = queue.length - 1;
  return <>
    {error ? <div className="container"><ErrorState message={getUserFacingError(error)} onRetry={() => { setError(null); setRetry((value) => value + 1); }} /></div> : null}
    {current ? <NoticeModal key={current.key} notice={current.notice} dismissible={!current.restriction} wide={current.restriction} closeLabel={remaining > 0 ? t("dashboard.notice_next", { count: remaining }) : t("dashboard.notice_ok")} onClose={() => setQueue((items) => items.slice(1))} /> : null}
  </>;
}
